<template>
  <div class="c-partnerships-settings">
    <BittsModal
      :width="1400"
      sidebar-title="Settings"
      :title="title"
      :description="description"
      variant="sidebar"
      :loading="loading"
      @closed="close"
      @action="handleAction"
      :use-mask-to-close="true"
      :disabled="saving || loading || !hasChanges"
      :action-loading="saving"
      :show-primary-button="tab !== PartnershipSettingsTabType.SHARED_WITH_YOU"
      :hide-footer="
        !hasManageVisibilityPermission &&
        tab === PartnershipSettingsTabType.TEAM_ACCESS
      "
      :no-padding="true"
      class="c-partnership-settings-modal"
    >
      <template #sidebar-header-prefix>
        <PartnerIcon :partner-uuid="partner?.uuid" class="mr-12" />
      </template>
      <template #sidebar-footer>
        <BittsButton
          v-if="hasManagePartnerInvitePermission"
          :disabled="saving"
          type="danger"
          text="Delete partnership"
          variant="ghost"
          :left-icon="['fak', 'delete']"
          data-testid="c-settings-delete"
          @click="showDeleteModal"
        />
      </template>
      <template #sidebar-content>
        <div
          v-for="(item, i) in sidebarItems"
          :key="item.tab"
          @click="handleTabClick(item.tab)"
          class="c-modal-sidebar-link"
          :class="{
            active: tab === item.tab,
            'mt-auto': i === sidebarItems.length - 1,
          }"
        >
          <FontAwesomeIcon
            class="c-modal-sidebar-link__icon"
            :icon="item.icon"
            :style="{ height: '20px', width: '20px' }"
          />
          <span class="c-modal-sidebar-link__label">{{ item.label }}</span>
        </div>
      </template>
      <div class="c-partnerships-settings__content">
        <component :is="TabComponent" class="min-h-[600px]" v-bind="tabProps" />
      </div>
    </BittsModal>
    <router-view-wrapper />
  </div>
</template>

<script setup lang="ts">
import { BittsButton, BittsModal } from '@crossbeam/bitts';

import { storeToRefs } from 'pinia';
import { computed, provide, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import type { ModalSidebarItem } from '@/components/ModalSidebarLink.vue';
import SharedWithYouTab from '@/components/partners/DataSharingSettings/SettingsModal/SharedWithYouTab.vue';
import MyDataSharingTab from '@/components/partners/DataSharingSettings/SettingsModal/SharingSettingsTab/components/MyDataSharingTab.vue';
import TeamAccessTab from '@/components/partners/DataSharingSettings/SettingsModal/TeamAccessTab.vue';
import PartnerIcon from '@/components/partners/PartnerIcon.vue';

import useAuth from '@/composables/useAuth';
import { usePartnershipTeamAccess } from '@/composables/usePartnershipTeamAccess';
import { TEMP_PARTNER_VISIBILITY } from '@/constants/feature_flags';
import {
  useBillingStore,
  useFeatureFlagStore,
  usePartnersStore,
} from '@/stores';
import type { SegmentType } from '@/types/populations';

import { PartnershipSettingsTabType } from './constants';

const props = withDefaults(
  defineProps<{
    partnerId?: number;
    initialTab?: PartnershipSettingsTabType;
    initialOverridesFilter?: boolean;
    initialSegment?: SegmentType;
  }>(),
  {
    partnerId: undefined,
    initialTab: undefined,
    initialOverridesFilter: undefined,
    initialSegment: undefined,
  },
);

const emit = defineEmits<{
  close: [];
}>();

const router = useRouter();
const route = useRoute();
const partnersStore = usePartnersStore();
const featureFlagStore = useFeatureFlagStore();
const { isEnterpriseTier } = storeToRefs(useBillingStore());
const { hasPermission } = useAuth();
const hasManageVisibilityPermission = computed(() =>
  hasPermission('manage:partner-visibility'),
);
const hasManagePartnerInvitePermission = computed(() =>
  hasPermission('manage:partnerships'),
);

const isPartnerVisibilityEnabled = computed(
  () =>
    featureFlagStore.hasFeatureFlag(TEMP_PARTNER_VISIBILITY) ||
    isEnterpriseTier.value,
);

const isPropsMode = computed(() => props.partnerId !== undefined);

const localTab = ref<PartnershipSettingsTabType>(
  props.initialTab ?? PartnershipSettingsTabType.SHARING_SETTINGS,
);

const pendingOverridesFilter = ref<boolean | undefined>(
  props.initialOverridesFilter,
);

const pendingSegment = ref<SegmentType | undefined>(props.initialSegment);

const tab = computed(() =>
  isPropsMode.value
    ? localTab.value
    : (route.params.tab as PartnershipSettingsTabType),
);

const partnerId = computed(
  () => props.partnerId ?? Number(route.params.partner_org_id as string),
);

// Provide partner ID to child components when in props mode
provide(
  'propsPartnerId',
  computed(() => (isPropsMode.value ? partnerId.value : null)),
);

const partner = computed(
  () => partnersStore.getPartnerOrgById(partnerId.value, false) || null,
);

const isOfflinePartner = computed(() => partner.value?.offline_partner);
const isManagedOfflinePartner = computed(() => partner.value?.virtual_partner);

const {
  close: closeTeamAccess,
  handleSave: handleSaveTeamAccess,
  hasChanges: hasChangesPartnershipTeamAccess,
} = usePartnershipTeamAccess(partner.value?.uuid);

function close() {
  if (isPropsMode.value) {
    emit('close');
  } else {
    closeTeamAccess();
  }
}

const loading = ref(false);
const saving = ref(false);

const sidebarItems = computed(
  () =>
    [
      {
        label: 'Shared with Partner',
        icon: ['fak', 'shared-with-partner'],
        tab: PartnershipSettingsTabType.SHARING_SETTINGS,
      },
      !isOfflinePartner.value &&
        !isManagedOfflinePartner.value && {
          label: 'Shared with You',
          icon: ['fak', 'shared-with-you'],
          tab: PartnershipSettingsTabType.SHARED_WITH_YOU,
        },
      isPartnerVisibilityEnabled.value && {
        label: 'Team Access',
        icon: ['fak', 'team'],
        tab: PartnershipSettingsTabType.TEAM_ACCESS,
      },
    ].filter(Boolean) as ModalSidebarItem<PartnershipSettingsTabType>[],
);

const TabComponent = computed(() => {
  switch (tab.value) {
    case PartnershipSettingsTabType.SHARED_WITH_YOU:
      return SharedWithYouTab;
    case PartnershipSettingsTabType.TEAM_ACCESS:
      return TeamAccessTab;
    case PartnershipSettingsTabType.SHARING_SETTINGS:
    default:
      return MyDataSharingTab;
  }
});

const tabProps = computed(() => {
  switch (tab.value) {
    case PartnershipSettingsTabType.SHARING_SETTINGS:
      return {
        partnerId: partnerId.value,
        initialOverridesFilter: pendingOverridesFilter.value,
        initialSegment: pendingSegment.value,
        ref: sharingSettingsTabRef,
      };
    case PartnershipSettingsTabType.SHARED_WITH_YOU:
      return {
        partnerId: partnerId.value,
        isPropsMode: isPropsMode.value,
      };
    case PartnershipSettingsTabType.TEAM_ACCESS:
    default:
      return { partnerUuid: partner.value?.uuid };
  }
});

const title = computed(() => {
  switch (tab.value) {
    case PartnershipSettingsTabType.SHARING_SETTINGS:
      return `Sharing data with ${partner.value?.name}`;
    case PartnershipSettingsTabType.TEAM_ACCESS:
      return `${hasManageVisibilityPermission.value ? 'Manage' : 'View'} Access`;
    default:
      return `Data shared by ${partner.value?.name}`;
  }
});

const description = computed(() => {
  switch (tab.value) {
    case PartnershipSettingsTabType.SHARING_SETTINGS:
      return '';
    case PartnershipSettingsTabType.TEAM_ACCESS:
      return `${hasManageVisibilityPermission.value ? 'Edit' : 'View'} roles and people who have access to ${partner.value?.name} in Crossbeam`;
    default:
      return '';
  }
});

const sharingSettingsTabRef = ref<InstanceType<typeof MyDataSharingTab> | null>(
  null,
);

const hasChanges = computed(() => {
  if (tab.value === PartnershipSettingsTabType.SHARING_SETTINGS) {
    return sharingSettingsTabRef.value?.hasChanges;
  }
  return hasChangesPartnershipTeamAccess.value;
});

async function handleAction() {
  switch (tab.value) {
    case PartnershipSettingsTabType.SHARING_SETTINGS: {
      saving.value = true;
      await sharingSettingsTabRef.value?.triggerBulkUpdate();
      saving.value = false;
      break;
    }
    case PartnershipSettingsTabType.TEAM_ACCESS:
      await handleSaveTeamAccess();
      break;
    default:
      break;
  }
}

function handleTabClick(newTab: PartnershipSettingsTabType) {
  pendingOverridesFilter.value = undefined;
  pendingSegment.value = undefined;
  if (isPropsMode.value) {
    localTab.value = newTab;
  } else {
    router.push({
      name: 'partnership-settings',
      params: { ...route.params, tab: newTab },
    });
  }
}

async function showDeleteModal() {
  const cancelDestination = route.path;
  await router.push({ name: 'delete_partner', query: { cancelDestination } });
}
</script>

<style lang="pcss" scoped>
@reference "../../../../css/theme.css";
.c-partnership-settings-modal.o-bitts-modal.open {
  @apply z-1030; /* Go under ant modal */
}

.c-partnerships-settings {
  @apply py-24 px-0;
}

.c-partnerships-settings__content {
  @apply flex flex-col;
}

.c-modal-sidebar-link__label {
  @apply opacity-75 font-bold relative;
}

.c-modal-sidebar-link {
  @apply flex items-center gap-10 py-10 w-full rounded-8 relative pl-10;

  .c-modal-sidebar-link__icon {
    @apply opacity-50;
  }

  &:focus-visible {
    @apply outline-hidden outline-offset-2 outline-2 outline-neutral-border-focus;
  }

  &:hover,
  &.active {
    @apply cursor-pointer bg-secondary-background-weak;
    .c-modal-sidebar-link__icon {
      @apply text-secondary-text;
    }
  }

  &.active .c-modal-sidebar-link__icon {
    @apply opacity-100;
  }
}
</style>
